// src/sim/moves.ts — Hamle FSM (ARCHITECTURE §3 adım 4; DESIGN §2 bağlam tablosu, §4 ortak kurallar, §11 nişan yardımı, §4.6 kök hareket):
// tetik çözümü (sütun önceliği Taşırken > Yerdeyken(knockdown.ts) > Havada > Rakip yerde (B → G3) > Koşarken > Yerde; B aday önceliği
// yatan kaldır > ayakta tut > eşya al; blok tutarken X/Y/B/R1), faz ilerletme (windup → active → recover → bitiş), `stages`, `holdBranch`
// (ıska takibi 15. t, moveTick korunur), `landPhase/landSyncTime`, iptal pencereleri (rel start | recover | hit; hit+2) + tampon tüketimi,
// whiff cezası Active kapanınca (×1.4; Flying Kick faceplant zinciri), aile kilitleri, selfPush, kök hareket → rmDx/rmDz.
// Dodge / blok / taunt girdileri de burada (systems.tryDodge / systems.tryBlock). three'siz; sıcak yolda tahsis yok.
//
// Durumlar: Attack (tüm saldırılar; Carry omuz çarpması dâhil — kurban sokette kalır), GrabAttempt (G1: aynı FSM, hitbox teması grab.tryGrab),
// Taunt (T hamlesi; 9 t'den sonra girdiyle iptal). Hold/Throw/Lift/Carry/PickUp durumları grab.ts'de. Lying girdileri knockdown.ts'de
// (`getUpKick` bayrağı → burada K_getup_kick başlatılır).
import { Btn, Ev, FState, MovePhase } from '../core/types.ts';
import type { Fighter, InputFrame, MoveDef, MoveFamily, SimWorld, WeaponKind } from '../core/types.ts';
import { fwdX, fwdZ, wrapAngle, yawOf } from '../core/vec3.ts';
import { clamp } from '../core/math.ts';
import { NO_ID, NO_TICK, TICK_DT } from '../config/constants.ts';
import { btnIndex } from '../config/controls.ts';
import { T } from '../config/tuning.ts';
import { CONTEXT_MOVES, HOLD_BRANCH_BY_WEAPON, MOVE_ID, moveIndexOf } from '../config/moves.ts';
import type { TriggerContext } from '../config/moves.ts';
import { releaseBlockForAttack, stepBlock } from './block.ts';
import { findLiftTarget, grabbable, startPickUp } from './grab.ts';
import { clipIdxOf, stageIndexFor, stageOf, stageStartMoveTick } from './hitbox.ts';
import { clearMove, enterFaceplant } from './knockdown.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const FAMILIES: readonly MoveFamily[] = ['light', 'heavy', 'grab', 'dash', 'air', 'lying', 'carry', 'taunt', 'weapon'];
const familyIndex = (f: MoveFamily): number => FAMILIES.indexOf(f);
const IDX_T = moveIndexOf(MOVE_ID.T);
const IDX_K = moveIndexOf(MOVE_ID.GETUP_KICK);
const IDX_SHIELD_BASH = moveIndexOf(MOVE_ID.SHIELD_BASH);

// ───────────────────────────── tampon yardımcıları (fighter.ts import edilmez) ─────────────────────────────
function pressedWithin(world: SimWorld, f: Fighter, btn: Btn, window: number): boolean {
  const t = world.buffers[f.id]!.pressTick[btnIndex(btn)]!;
  return t !== NO_TICK && world.tick - t <= window;
}
function consume(world: SimWorld, f: Fighter, btn: Btn): void { world.buffers[f.id]!.pressTick[btnIndex(btn)] = NO_TICK; }
const heldHeavy = (input: InputFrame): boolean => (input.buttons & Btn.Heavy) !== 0;
/** Koşma bağlamı (DESIGN §2): hız ≥ 5.5 ∧ çubuk ≥ 0.85 en az 10 t (sayaç fighter.ts adım 3'te). */
export const inRunContext = (f: Fighter, tick: number): boolean =>
  f.runContextSince !== NO_TICK && tick - f.runContextSince >= T.MOVE.runContextTicks;

// ───────────────────────────── nişan yardımı (DESIGN §11) ─────────────────────────────
/** En iyi hedef: menzil, koni, dikey fark; skor = dot·2 − d/range. Döner id ya da NO_ID.
 *  halfConeDeg = YARI açı: T.AIM.*ConeDeg değerleri "±" anlamlıdır (DESIGN §11 koni ±60°, tutma ±80°, fırlatma ±35°) — tam koni
 *  sayılınca yumruk ±30°, tutma ±40° kalıyordu (düzeltici tur 2; bot brain.ts ATTACK_HALF_CONE aynı yarı açıyı kullanmalı). */
export function aimTarget(world: SimWorld, f: Fighter, range: number, halfConeDeg: number, maxH: number): number {
  const cosHalf = Math.cos(halfConeDeg * Math.PI / 180);
  const fx = fwdX(f.yaw), fz = fwdZ(f.yaw);
  let best = NO_ID, bestScore = -Infinity;
  for (let i = 0; i < world.fighters.length; i++) {
    const b = world.fighters[i]!;
    if (b === f) continue;
    const s = b.state;
    if (s === FState.Spawning || s === FState.Respawning || s === FState.Falling || s === FState.Eliminated) continue;
    const dx = b.pos.x - f.pos.x, dz = b.pos.z - f.pos.z;
    const d = hyp(dx, dz);
    if (d > range || Math.abs(b.pos.y - f.pos.y) > maxH) continue;
    const dot = d > 1e-6 ? (dx * fx + dz * fz) / d : 1;
    if (dot < cosHalf) continue;
    const score = dot * T.AIM.scoreDotWeight - d / range;
    if (score > bestScore) { bestScore = score; best = b.id; }
  }
  return best;
}
/** Hamle başında bir kez: aimYaw geçerliyse imleç yönü, değilse bakış + yardım (hedefe dön). */
function aimAtStart(world: SimWorld, f: Fighter, input: InputFrame, range: number, halfConeDeg: number): void {
  if (Number.isFinite(input.aimYaw)) { f.yaw = wrapAngle(input.aimYaw); return; }
  const id = aimTarget(world, f, range, halfConeDeg, T.AIM.maxHeightDiff);
  if (id !== NO_ID) { const b = world.fighters[id]!; f.yaw = yawOf(b.pos.x - f.pos.x, b.pos.z - f.pos.z); }
}

// ───────────────────────────── başlat / bitir ─────────────────────────────
/** Hamle başlat (bağlam çözüldükten sonra). branch = ıska takibi geçişi (moveTick korunur, sfx tekrar çalmaz). */
export function startMove(world: SimWorld, f: Fighter, idx: number, input: InputFrame, branch: boolean = false): void {
  const tick = world.tick;
  const m = world.moves[idx]!;
  const keepTick = branch ? f.moveTick : 0;
  const wasBlock = f.state === FState.Block;
  if (!branch) {
    if (f.invulnUntil > tick && f.state !== FState.Lying && f.state !== FState.Crawl) f.invulnUntil = NO_TICK;   // kalkış koruması kendi saldırısıyla biter
    if (wasBlock) releaseBlockForAttack(f);
    if (m.family !== 'lying') aimAtStart(world, f, input, m.family === 'grab' ? T.AIM.grabRange : T.AIM.attackRange, m.family === 'grab' ? T.AIM.grabConeDeg : T.AIM.attackConeDeg);
  }
  clearMove(f);
  f.moveIdx = idx; f.moveTick = keepTick; f.stageIdx = branch ? 1 : 0; f.stageStartTick = tick;
  f.phase = m.windup > keepTick ? MovePhase.Windup : MovePhase.Active;
  f.dodgeDirX = fwdX(f.yaw); f.dodgeDirZ = fwdZ(f.yaw);   // ölçekle kök hareket yönü = hamle başındaki bakış
  f.blocking = false;
  const s = m.family === 'grab' ? FState.GrabAttempt : m.family === 'taunt' ? FState.Taunt : FState.Attack;
  f.state = s; f.stateTick = tick; f.stateUntil = NO_TICK;
  if (!branch) {
    // yatay hız ×0.6 YALNIZ hava jab'larda (A1 ve silahlı hava X türevleri: usesPerAir tanımlı) — DESIGN §4.1 bunu A1 için tanımlar;
    // A2 Flying Kick / Dive Chop / dual WA-Y hızını korur (dalış kısalmasın; Flying Kick selfPush'a bağımlı kalmasın).
    if (m.usesPerAir !== undefined) { f.airJabLeft--; f.vel.x *= T.MOVE.airAttackSpeedMult; f.vel.z *= T.MOVE.airAttackSpeedMult; }
    if (m.landPhase === 'windup' && m.selfPushVy !== undefined) f.vel.y = Math.min(f.vel.y, m.selfPushVy);   // dalış (Dive Chop vy −14)
    if (m.id === MOVE_ID.GETUP_KICK) f.invulnUntil = tick + T.KNOCKDOWN.getUpKickTicks;   // ilk aşama i-frame (vuruş + kaldırma)
    if (m.family === 'taunt') world.events.push(Ev.Taunt, tick, f.id);
  }
  if (f.phase === MovePhase.Active && !branch) enterActive(f, m);
  const e = world.events.push(Ev.MoveStart, tick, f.id); e.move = idx; e.v0 = branch ? 1 : 0;
}
function enterActive(f: Fighter, m: MoveDef): void {
  f.phase = MovePhase.Active;
  if (m.selfPush !== 0) { f.vel.x += fwdX(f.yaw) * m.selfPush; f.vel.z += fwdZ(f.yaw) * m.selfPush; }
  if (m.selfPushVy !== undefined && m.landPhase !== 'windup') f.vel.y += m.selfPushVy;
}
/** Hamle bitti: kilit, son hamle kaydı, durum (Carry'ye dönüş / Idle / Air). */
export function endMove(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  const m = f.moveIdx >= 0 ? world.moves[f.moveIdx] : undefined;
  f.lastMoveIdx = f.moveIdx; f.lastMoveEndTick = tick;
  if (m?.lockAfter) { f.lockFamily = familyIndex(m.lockAfter.family); f.lockUntil = tick + m.lockAfter.ticks; }
  clearMove(f);
  if (f.grabTarget !== NO_ID) { const v = world.fighters[f.grabTarget]; if (v && v.state === FState.Carried) { f.state = FState.Carrying; f.stateTick = tick; f.stateUntil = NO_TICK; return; } }
  f.state = f.grounded ? FState.Idle : FState.Air; f.stateTick = tick; f.stateUntil = NO_TICK;
}

// ───────────────────────────── kök hareket (ARCHITECTURE §4.6) ─────────────────────────────
/** Kök hareket hangi aşamada: scaled → stages[0] (Dual WH Dodge_Forward), extract → son aşama (D2 Jump_Chop); aşamasız → 0. */
function rmStageOf(m: MoveDef): number {
  if (!m.stages) return 0;
  return m.rootMotion.mode === 'scaled' ? 0 : m.stages.length - 1;
}
function applyRootMotion(world: SimWorld, f: Fighter, m: MoveDef): void {
  const rm = m.rootMotion;
  if (rm.mode === 'none') return;
  if (f.stageIdx !== rmStageOf(m)) return;
  const st = stageOf(m, f.stageIdx);
  // clipIdx çözümü hitbox.ts üzerinden: `clipIdx`/`clipEnd` PAYLAŞILAN MoveDef nesnelerine yazılıyor ve burada
  // doğrudan yazmak, farklı bir ClipDataSet taşıyan ikinci bir dünyayı yakalayan korumayı atlıyordu (NETWORK.md §5).
  if (clipIdxOf(world, st) < 0) return;
  const k = f.moveTick - stageStartMoveTick(m, f.stageIdx);
  if (k < 1) return;
  const rate = st.clipRate;
  const tPrev = st.clipStart + (k - 1) * TICK_DT * rate, tNow = st.clipStart + k * TICK_DT * rate;
  const t0 = rm.window[0], t1 = rm.window[1];
  if (!(tNow > t0 && tPrev < t1)) return;
  const clips = world.clips;
  const cd = clips.byIndex(st.clipIdx);
  const fa = clips.frameOf(st.clipIdx, clamp(tPrev, t0, t1)), fb = clips.frameOf(st.clipIdx, clamp(tNow, t0, t1));
  if (fa === fb) return;
  f.rootFrame = fb;
  if (rm.mode === 'extract') {
    const dF = cd.lungeFwd[fb]! - cd.lungeFwd[fa]!, dL = cd.lungeLeft[fb]! - cd.lungeLeft[fa]!;
    const s = Math.sin(f.yaw), c = Math.cos(f.yaw);
    f.rmDx += (dF * s + dL * c) * rm.mult; f.rmDz += (dF * c - dL * s) * rm.mult;
  } else {
    const ax = cd.lungeAxis[0], az = cd.lungeAxis[1];
    const fs = clips.frameOf(st.clipIdx, t0), fe = clips.frameOf(st.clipIdx, t1);
    const den = (cd.lungeFwd[fe]! * ax + cd.lungeLeft[fe]! * az) - (cd.lungeFwd[fs]! * ax + cd.lungeLeft[fs]! * az);
    if (Math.abs(den) < 1e-6) return;
    const ds = ((cd.lungeFwd[fb]! * ax + cd.lungeLeft[fb]! * az) - (cd.lungeFwd[fa]! * ax + cd.lungeLeft[fa]! * az)) * rm.distance / den;
    f.rmDx += f.dodgeDirX * ds; f.rmDz += f.dodgeDirZ * ds;
  }
}

// ───────────────────────────── tetik çözümü (DESIGN §2) ─────────────────────────────
const IDX_FAMILY_HEAVY = familyIndex('heavy');
/** Aile kilidi: aynı aile ∨ 'heavy' kilidi altında her `heavy` bayraklı hamle (silahlı ağır/bitiriciler family 'weapon' — DESIGN §5
 *  "ağırlardan sonra 18 t aile kilidi" silahlıyken de tutsun; silahlı hafif seri adımları (heavy false) kilitlenmez). */
function locked(f: Fighter, m: MoveDef, tick: number): boolean {
  if (tick >= f.lockUntil) return false;
  return f.lockFamily === familyIndex(m.family) || (f.lockFamily === IDX_FAMILY_HEAVY && m.heavy);
}
function contextOf(f: Fighter, tick: number): TriggerContext {
  if (f.state === FState.Carrying) return 'carrying';
  if (!f.grounded || f.state === FState.Air) return 'air';
  if (inRunContext(f, tick)) return 'running';
  return 'ground';
}
/** Bağlam tablosundan hamle indeksi; '' → −1. */
function tableMove(ctx: TriggerContext, weapon: WeaponKind, btn: 'light' | 'heavy' | 'grab'): number {
  const id = CONTEXT_MOVES[ctx][weapon][btn];
  return id === '' ? -1 : moveIndexOf(id);
}
/** Bir hamleyi bağlama göre başlatmayı dener (tampon tüketir). true → başladı. */
function tryStart(world: SimWorld, f: Fighter, input: InputFrame, idx: number, btn: Btn): boolean {
  if (idx < 0) return false;
  const m = world.moves[idx]!;
  if (locked(f, m, world.tick)) return false;
  if (m.usesPerAir !== undefined && f.airJabLeft <= 0) return false;
  consume(world, f, btn);
  startMove(world, f, idx, input);
  return true;
}
/** B: silahsız → yatan kaldır (≤ 1.2 u) > ayakta tut (1.6 u/±80° aday ya da whiff) > eşya al; silahlı → (1H: kalkan al) / fırlat. */
function tryGrabButton(world: SimWorld, f: Fighter, input: InputFrame, ctx: TriggerContext): boolean {
  const tick = world.tick;
  if (ctx === 'air' || ctx === 'carrying') return false;
  if (f.weapon === 'unarmed') {
    // ÜSTÜNDE DURDUĞUN SİLAH ÖNCE GELİR: eşya çok yakınsa (T.ITEMS.pickupFirstRange) yatan rakipten de önce alınır.
    // Sebep (kullanıcı bulgusu): kalabalıkta B'ye basınca silah yerine hep birini kaldırıyordun; oyuncu ayağının
    // dibindeki kılıcı kastediyorsa niyeti açıktır. Daha uzaktaki eşya eski sırada kalır (yatan > tutma > eşya).
    if (world.systems.items.tryPickup(world, f, tick, T.ITEMS.pickupFirstRange)) { consume(world, f, Btn.Grab); return true; }
    const lying = findLiftTarget(world, f);
    if (lying) { consume(world, f, Btn.Grab); if (f.state === FState.Block) releaseBlockForAttack(f); startPickUp(world, f, lying); return true; }
    const cand = aimTarget(world, f, T.AIM.grabRange, T.AIM.grabConeDeg, T.AIM.maxHeightDiff);
    const candOk = cand !== NO_ID && grabbable(world, world.fighters[cand]!);
    if (!candOk && world.systems.items.tryPickup(world, f, tick)) { consume(world, f, Btn.Grab); return true; }
    return tryStart(world, f, input, tableMove(ctx, 'unarmed', 'grab'), Btn.Grab);
  }
  // silahlı: 1H + yakın kalkan → al (items.ts kuralı); aksi hâlde fırlat (±35° / 8 u nişan)
  if (f.weapon === '1h' && !f.hasShield && world.systems.items.tryPickup(world, f, tick)) { consume(world, f, Btn.Grab); return true; }
  consume(world, f, Btn.Grab);
  if (f.state === FState.Block) releaseBlockForAttack(f);
  if (!Number.isFinite(input.aimYaw)) { const id = aimTarget(world, f, T.AIM.throwRange, T.AIM.throwConeDeg, T.AIM.maxHeightDiff); if (id !== NO_ID) { const b = world.fighters[id]!; f.yaw = yawOf(b.pos.x - f.pos.x, b.pos.z - f.pos.z); } }
  else f.yaw = wrapAngle(input.aimYaw);
  world.systems.items.throwWeapon(world, f, fwdX(f.yaw), fwdZ(f.yaw), tick);
  return true;
}
/**
 * Boş durumda tetikler: R1 dodge → B → Y → X → taunt → L1 blok. Döner: bir şey başladı mı.
 * allow: hangi tuşlar açık (iptal pencerelerinden gelen bit maskesi; boş durumda hepsi).
 */
function tryTriggers(world: SimWorld, f: Fighter, input: InputFrame, allow: number): boolean {
  const tick = world.tick;
  if (f.controlLockUntil > tick) return false;
  const ctx = contextOf(f, tick);
  if (ctx === 'air' && f.airDodgeNoAttackUntil > tick) { /* hava dodge sonrası saldırı yasağı */ }
  else {
    if ((allow & Btn.Grab) && pressedWithin(world, f, Btn.Grab, T.INPUT.grabBufferTicks) && tryGrabButton(world, f, input, ctx)) return true;
    if ((allow & Btn.Heavy) && pressedWithin(world, f, Btn.Heavy, T.INPUT.bufferTicks)) {
      if (tryStart(world, f, input, tableMove(ctx, f.weapon, 'heavy'), Btn.Heavy)) return true;
    }
    if ((allow & Btn.Light) && pressedWithin(world, f, Btn.Light, T.INPUT.bufferTicks)) {
      const idx = f.state === FState.Block && f.blocking && f.hasShield ? IDX_SHIELD_BASH : tableMove(ctx, f.weapon, 'light');
      if (tryStart(world, f, input, idx, Btn.Light)) return true;
    }
  }
  if ((allow & Btn.Dodge) && world.systems.tryDodge(world, f, input)) return true;
  if ((allow & Btn.Taunt) && ctx === 'ground' && (f.state === FState.Idle || f.state === FState.Move) && pressedWithin(world, f, Btn.Taunt, T.INPUT.bufferTicks)) {
    consume(world, f, Btn.Taunt); startMove(world, f, IDX_T, input); return true;
  }
  if ((allow & Btn.Block) && world.systems.tryBlock(world, f, input)) return true;
  return false;
}

// ───────────────────────────── faz ilerletme ─────────────────────────────
const ALL_BTNS = Btn.Light | Btn.Heavy | Btn.Grab | Btn.Dodge | Btn.Jump | Btn.Block | Btn.Taunt;
const TAU = Math.PI * 2;
const TURN_STEP = T.MOVE.turnRate * TICK_DT;   // recover'da dönüş 14 rad/s → tick başına
const MOVE_BIT = Btn.Start;   // 'move' iptali için yerel bit (Start hamle tetiği değil)

/** Etkin recover uzunluğu (whiff ×1.4; landPhase 'recover' inişte JUMP.landTicks). */
function recoverTicks(m: MoveDef, f: Fighter): number {
  if (m.landPhase === 'recover' && f.grounded) return T.JUMP.landTicks;
  return f.whiffed ? Math.round(m.recover * m.whiffRecoverMult) : m.recover;
}
/** İptal pencerelerinden açık tuş maskesi ve 'next' hedefi. */
let nextIdx = -1;
function openCancels(m: MoveDef, f: Fighter, recoverStart: number): number {
  let mask = 0; nextIdx = -1;
  if (m.landPhase === 'recover' && f.grounded && f.phase === MovePhase.Recover) return ALL_BTNS;   // inişte hamle bitti sayılır: saldırı hemen
  for (let i = 0; i < m.cancels.length; i++) {
    const c = m.cancels[i]!;
    let open: boolean;
    if (c.rel === 'hit') open = f.firstHitTick !== NO_TICK && f.moveTick >= f.firstHitTick + c.at;
    else if (c.rel === 'recover') open = f.phase === MovePhase.Recover && f.moveTick >= recoverStart + c.at;
    else open = f.moveTick >= c.at;
    if (!open) continue;
    if (c.onTouch && !f.touched) continue;
    switch (c.into) {
      case 'next': if (c.next && f.touched) { mask |= Btn.Light; if (nextIdx < 0) nextIdx = moveIndexOf(c.next); } break;
      case 'light': mask |= Btn.Light; break;
      case 'heavy': mask |= Btn.Heavy; break;
      case 'grab': mask |= Btn.Grab; break;
      case 'dodge': mask |= Btn.Dodge; break;
      case 'jump': mask |= Btn.Jump; break;
      case 'block': mask |= Btn.Block; break;
      case 'move': mask |= MOVE_BIT; break;
      default: break;
    }
  }
  return mask;
}

function stepAttack(world: SimWorld, f: Fighter, input: InputFrame): void {
  const tick = world.tick;
  let m = world.moves[f.moveIdx];
  if (!m) { endMove(world, f); return; }

  // ── ilerlet ──
  if (f.phase === MovePhase.Windup && m.landPhase === 'windup') {
    if (f.grounded) { f.moveTick = m.windup; f.stageStartTick = tick; enterActive(f, m); }
    else if (f.moveTick < m.windup - 1) f.moveTick++;
  } else f.moveTick++;

  // ── ıska takibi (15. t: Active kapandı, dokunma yok, Y basılı) ──
  if (m.holdBranch && f.moveTick === T.INPUT.heavyHoldTicks && !f.touched && heldHeavy(input) && f.grounded) {
    let target = m.holdBranch;
    const byW = HOLD_BRANCH_BY_WEAPON[target];
    if (byW) { const t = byW[f.weapon]; if (t) target = t; }
    const idx = moveIndexOf(target);
    if (idx >= 0) { startMove(world, f, idx, input, true); m = world.moves[idx]!; }
  }

  // ── aşama ──
  if (m.stages) {
    const si = stageIndexFor(m, f.moveTick);
    if (si !== f.stageIdx) { f.stageIdx = si; f.stageStartTick = tick; const e = world.events.push(Ev.PhaseChange, tick, f.id); e.move = f.moveIdx; e.v0 = si; }
  }

  // ── faz geçişleri ──
  const activeEnd = m.windup + m.active;
  if (f.phase === MovePhase.Windup && f.moveTick >= m.windup) { enterActive(f, m); const e = world.events.push(Ev.PhaseChange, tick, f.id); e.move = f.moveIdx; e.v1 = MovePhase.Active; }
  if (f.phase === MovePhase.Active) {
    const landed = m.landPhase === 'active' && f.grounded && f.moveTick > m.windup;
    if (landed) f.moveTick = activeEnd;
    if (f.moveTick >= activeEnd) {
      f.phase = MovePhase.Recover;
      const e = world.events.push(Ev.PhaseChange, tick, f.id); e.move = f.moveIdx; e.v1 = MovePhase.Recover;
      if (!f.touched && m.active > 0) {
        // whiff: recover ×1.4 hemen (Active kapanınca); Flying Kick → yüzüstü zincir
        f.whiffed = true;
        const w = world.events.push(Ev.Whiff, tick, f.id); w.move = f.moveIdx; w.v0 = m.onWhiff === 'faceplant' ? 1 : 0;
        if (m.onWhiff === 'faceplant') { f.lastMoveIdx = f.moveIdx; f.lastMoveEndTick = tick; enterFaceplant(world, f); return; }
      }
    }
  }
  if (m.landPhase === 'recover') {
    // hava hamlesi: havada bitmez; inişte hamle biter sayılır → Jump_Land JUMP.landTicks 8 t hareket kilidi, saldırı hemen (stageIdx 1 = indi işareti)
    if (f.grounded) {
      if (f.stageIdx !== 1) { f.stageIdx = 1; f.stageStartTick = tick; f.phase = MovePhase.Recover; f.moveTick = activeEnd; if (!f.touched) f.whiffed = true; }
      else if (f.moveTick >= activeEnd + T.JUMP.landTicks) { endMove(world, f); return; }
    }
  } else if (f.phase === MovePhase.Recover && f.moveTick >= activeEnd + recoverTicks(m, f)) { endMove(world, f); return; }

  // ── recover'da dönüş (14 rad/s), w+a'da yön kilidi ──
  // (yardımcılar yerinde: stepAttack büyük olduğundan V8 hyp/yawOf/yawDelta/wrapAngle çağrılarını inline etmiyor, double argüman/dönüşleri
  //  HeapNumber'a kutuluyordu — sıcak yolda tahsis; düzeltici tur 2)
  const mx = input.moveX, mz = input.moveZ;
  const stickMag = Math.sqrt(mx * mx + mz * mz);
  if (f.phase === MovePhase.Recover && !m.keepMomentum && stickMag > T.MOVE.walkDead) {
    const target = Math.atan2(mx, mz);
    let d = (target - f.yaw) % TAU; if (d > Math.PI) d -= TAU; else if (d <= -Math.PI) d += TAU;
    const step = TURN_STEP;
    if (Math.abs(d) <= step) f.yaw = target;
    else { let y = (f.yaw + (d > 0 ? step : -step)) % TAU; if (y > Math.PI) y -= TAU; else if (y <= -Math.PI) y += TAU; f.yaw = y; }
  }

  // ── kök hareket ──
  applyRootMotion(world, f, m);

  // ── iptal pencereleri → tampon ──
  if (f.controlLockUntil > tick) return;
  const mask = openCancels(m, f, activeEnd);
  if (mask === 0) return;
  // DESIGN §5 "basılı tutma yalnız hafif seride tekrar üretir": 'next' penceresinde X BASILI da basış sayılır (Y/B/dodge için değil;
  // boş durumda tryTriggers yalnız basış kenarı okur → seri bitince tutmak yeni L1 üretmez)
  const lightHeld = (input.buttons & Btn.Light) !== 0;
  if ((mask & Btn.Light) && nextIdx >= 0 && (lightHeld || pressedWithin(world, f, Btn.Light, T.INPUT.bufferTicks))) {
    if (!locked(f, world.moves[nextIdx]!, tick)) {
      consume(world, f, Btn.Light);
      f.lastMoveIdx = f.moveIdx; f.lastMoveEndTick = tick;
      startMove(world, f, nextIdx, input);
      return;
    }
  }
  if ((mask & MOVE_BIT) && stickMag > T.MOVE.walkDead) { endMove(world, f); return; }
  if ((mask & Btn.Jump) && pressedWithin(world, f, Btn.Jump, T.JUMP.bufferTicks) && f.grounded) { endMove(world, f); return; }   // zıplama adım 6'da (tampon korunur)
  const triggers = mask & (Btn.Light | Btn.Heavy | Btn.Grab | Btn.Dodge | Btn.Taunt);
  if (triggers !== 0) {
    // yeni hamle / dodge: tetik tutarsa kesilen hamlenin aile kilidi ve son hamle kaydı işlenir (tetik tutmazsa hamle sürer)
    const prevIdx = f.moveIdx;
    if (tryTriggers(world, f, input, triggers)) {
      const pm = world.moves[prevIdx];
      if (pm?.lockAfter) { f.lockFamily = familyIndex(pm.lockAfter.family); f.lockUntil = tick + pm.lockAfter.ticks; }
      f.lastMoveIdx = prevIdx; f.lastMoveEndTick = tick;
      return;
    }
  }
  if ((mask & Btn.Block) && (input.buttons & Btn.Block) !== 0 && f.grounded) { endMove(world, f); world.systems.tryBlock(world, f, input); }
}

// ───────────────────────────── adım 4 ─────────────────────────────
/** SimSystems.stepMoves — ARCHITECTURE §3 adım 4. Hitstop'ta çağrılmaz (world); yine de korunur. */
export function stepMoves(world: SimWorld, f: Fighter): void {
  if (f.hitstop > 0) return;
  const tick = world.tick;
  const input = world.inputs[f.id]!;
  stepBlock(world, f);
  switch (f.state) {
    case FState.Attack:
    case FState.GrabAttempt:
    case FState.Taunt:
      stepAttack(world, f, input);
      return;
    case FState.Lying:
    case FState.Crawl:
      if (f.getUpKick && f.controlLockUntil <= tick) { f.getUpKick = false; startMove(world, f, IDX_K, input); }
      return;
    case FState.Idle:
    case FState.Move:
    case FState.Air:
    case FState.Block:
    case FState.Carrying:
      tryTriggers(world, f, input, ALL_BTNS);
      return;
    case FState.Dodge:
      // 12 t'den sonra hafif saldırıyla iptal (DESIGN §3)
      if (tick - f.stateTick >= T.DODGE.lightCancelAt) tryTriggers(world, f, input, Btn.Light);
      return;
    default:
      return;
  }
}
